import React, { useCallback, useContext, useMemo } from 'react';
import {
  findMentionInput,
  getNodeString,
  getPlugin,
  getPluginOptions,
  MentionPlugin,
  TComboboxItem,
  usePlateEditorRef,
} from '@udecode/plate';
import { Combobox } from '@udecode/plate-ui-combobox';

import {
  getDefMemoriesOptionsNext,
  replaceIndexes,
} from 'src/presentation/components/pages/sidekick/threads/components/SidePanel/Components/Rule/helpers/memoriesHelper';
import { getMemoryOnSelectItem } from './getMemoryOnSelectItem';
import { MEMORY_PLUGIN_NAME } from './createMemorySelectorPlugin';
import { ThreadModuleContext } from 'src/presentation/components/pages/sidekick/threads/components/SidePanel/ThreadModuleContext';

const MemorySelectorCombobox = () => {
  const editor = usePlateEditorRef();

  const { definedMemories, extActionMems, typeSpecs } = useContext(ThreadModuleContext);
  const { trigger } = getPluginOptions<MentionPlugin>(editor, MEMORY_PLUGIN_NAME);

  const allMemories = useMemo(
    () => getDefMemoriesOptionsNext([], extActionMems, typeSpecs, definedMemories || {}, false),
    [definedMemories, extActionMems, typeSpecs]
  );

  const handleFilter = useCallback((filter: string) => {
    return (item: TComboboxItem<any>) => {
      const alteredFilter = replaceIndexes(filter);
      const itemKey = replaceIndexes(item.key);
      return itemKey.toLowerCase().startsWith(alteredFilter.toLowerCase());
    };
  }, []);

  const currentMentionInput = findMentionInput(editor);

  const items = useMemo(() => {
    if (!currentMentionInput) {
      return;
    }

    const filter = getNodeString(currentMentionInput[0]);

    const fields = filter.split('.');

    const props: string[] = [];

    allMemories
      .filter(key => fields.length < 2 || key.toLowerCase().startsWith(replaceIndexes(filter)))
      .forEach(key => {
        let text = key;
        if (fields.length > 0) {
          text = text.split('.')[fields.length - 1] || '';
        }

        if (!props.includes(text)) {
          props.push(text);
        }
      });

    const prefix = fields.length > 1 ? fields.slice(0, fields.length - 1).join('.') + '.' : '';

    return props.map(key => {
      const prop = replaceIndexes(prefix + key)
        .trim()
        .toLocaleLowerCase();

      const endDot = !allMemories.find(key => key.toLocaleLowerCase() === prop) ? '.' : '';

      return { key: prefix + key + endDot, text: key };
    });
  }, [currentMentionInput, allMemories]);

  const handleSelectItem = (editor, item) => {
    const currentMentionInput = findMentionInput(editor);
    if (!currentMentionInput) return;

    const text = getNodeString(currentMentionInput[0]);

    const addText = item.key.slice(text.length, item.key.length);

    editor.insertText(addText);

    if (!addText.endsWith('.')) {
      getMemoryOnSelectItem(getPlugin(editor, MEMORY_PLUGIN_NAME))(editor, { key: item.key, text: item.key });
    }
  };

  return (
    <Combobox
      id={MEMORY_PLUGIN_NAME}
      trigger={trigger!}
      controlled
      onSelectItem={handleSelectItem}
      items={items}
      filter={handleFilter}
    />
  );
};

export default MemorySelectorCombobox;
